import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { NavLink, NavLinkRenderProps } from 'react-router-dom';

import { StepProgressIndicator } from '@/components/atoms/StepProgressIndicator';
import { useTranslator } from '@/hooks/useTranslator';
import { Status } from '@/types/steps';

interface Props {
  readonly path?: string;
  readonly title: string;
  readonly status: Status;
}

export const ParentStepProgressLabel: React.FC<Props> = (props: Props): React.ReactElement => {
  const { path, title, status } = props;

  const tr = useTranslator();

  return (
    <Wrapper to={path}>
      {(renderProps: NavLinkRenderProps): React.ReactElement => {
        const color: string = renderProps.isActive ? 'primary.main' : 'custom.Inactive';

        return (
          <Box sx={styles.container} gap="20px">
            <StepProgressIndicator status={status} active={renderProps.isActive} />
            <Typography id={path} fontSize={16} color={color} lineHeight="24px" fontWeight={500}>
              {tr(title)}
            </Typography>
          </Box>
        );
      }}
    </Wrapper>
  );
};

interface WrapperProps {
  readonly to?: string;
  children?(props: NavLinkRenderProps): React.ReactNode;
}

const Wrapper: React.FC<WrapperProps> = (props: WrapperProps): React.ReactElement => {
  const { to, children } = props;

  if (to) {
    return (
      <NavLink role="button" aria-labelledby={to} to={['..', to].join('/')}>
        {children}
      </NavLink>
    );
  }

  return <>{children?.({ isActive: false, isPending: false, isTransitioning: false })}</>;
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    height: 48,
  },
};
